/**
 * Vercel Web Analytics URL redaction.
 *
 * Web Analytics records the raw URL of every page view, including path
 * segments and query parameters. Several of this app's URLs carry values that
 * must not reach a third-party dashboard:
 *
 *  - `/invite/:token` — the invite token is a live credential. `invites.token_hash`
 *    is the unique lookup key and the plaintext is Fernet-encrypted at rest
 *    precisely because possessing it grants access to sign-up.
 *  - `?invite_token=` — the same credential, in query form, on the OAuth hand-off.
 *  - `?session=` — a CV FlowSession id used to resume a generation run.
 *
 * Vercel's own guidance is to strip these in `beforeSend` rather than rely on
 * their filtering. We replace values with a placeholder instead of dropping the
 * event, so page-view counts for these routes stay intact.
 *
 * Kept pure and dependency-free so it is unit-testable in isolation.
 */

/** Query parameters whose values identify a user, a session, or grant access. */
const REDACTED_QUERY_PARAMS = ['invite_token', 'session']

/** Path segments whose *following* segment is sensitive, with its placeholder. */
const REDACTED_PATH_SEGMENTS: { segment: string; placeholder: string }[] = [
  { segment: 'invite', placeholder: '[token]' },
]

const PLACEHOLDER = '[redacted]'

/**
 * Strip sensitive values from a page-view URL, preserving its shape so routes
 * remain distinguishable in the dashboard.
 *
 * Accepts absolute or relative URLs. Anything unparseable is returned as-is
 * only when it contains nothing sensitive; otherwise the path is dropped
 * entirely, so a parsing failure can never leak a token.
 */
export function redactAnalyticsUrl(url: string): string {
  let parsed: URL
  try {
    // Base is only used to parse relative URLs; it is never emitted.
    parsed = new URL(url, 'https://placeholder.invalid')
  } catch {
    return PLACEHOLDER
  }

  for (const { segment, placeholder } of REDACTED_PATH_SEGMENTS) {
    // Match the segment anywhere in the path, not only as a prefix. A malformed
    // URL resolves against the parse base and can nest it — "://invite/tok"
    // becomes "/://invite/tok" — and a prefix-only check would miss that and
    // leak the token. Segments are hardcoded literals, so no escaping is needed.
    const pattern = new RegExp(`(^|/)${segment}/[^/]+`, 'g')
    parsed.pathname = parsed.pathname.replace(pattern, `$1${segment}/${placeholder}`)
  }

  for (const key of REDACTED_QUERY_PARAMS) {
    if (parsed.searchParams.has(key)) {
      parsed.searchParams.set(key, PLACEHOLDER)
    }
  }

  const isAbsolute = /^[a-z][a-z0-9+.-]*:\/\//i.test(url)
  return isAbsolute ? parsed.toString() : `${parsed.pathname}${parsed.search}${parsed.hash}`
}
